"use client";

import React from "react";
import { Tv, Smartphone, Laptop, Gamepad2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function SeamlessMultiscreen() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const devices = [{
    icon: <Smartphone className="size-8" />,
    label: "iOS App",
    description: "iOS 15.0+ / iPhone 12 or newer"
  }, {
    icon: <Smartphone className="size-8" />,
    label: "Android",
    description: "v10.0+ / Samsung, Pixel, OnePlus"
  }, {
    icon: <Laptop className="size-8" />,
    label: "Web Portal",
    description: "Chrome, Safari, Edge (Latest)"
  }, {
    icon: <Tv className="size-8" />,
    label: "Tablets",
    description: "iPadOS & Android Tablet Support"
  }];
  return <section data-section-id="68219" id="system-requirements" ref={sectionRef} className="bg-background py-24 overflow-hidden dark">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <div className={cn("flex flex-col gap-6 transition-all duration-700 delay-100 transform", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tighter leading-[1.1] uppercase">
              Built for. Your. <br />
              <span className="text-primary">Compatibility.</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl font-medium">
              HalfOrder is optimized for the fast-paced Toronto lifestyle. 
              Our lightweight app ensures you can find meal partners and split 
              costs instantly, whether you're on the subway or in a campus lounge. 
              Stay connected to the social split across all your primary devices.
            </p>

            <div className="flex flex-col gap-3 mt-2">
              {["Optimized for iOS 15+ and Android 10+", "Low latency matching in Toronto GTA", "Minimal battery and data consumption"].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                  <div className="flex items-center justify-center size-5 rounded-sm bg-primary/10 text-primary shrink-0">
                    <Check className="size-3" />
                  </div>
                  <span className="text-foreground/90 font-semibold tracking-tight">{item}</span>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="rounded-sm px-8 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all">
                <Link to="/DownloadPage">Download App</Link>
              </Button>
            </div>
          </div>

          <div className={cn("relative flex justify-center lg:justify-end transition-all duration-700 delay-300 transform", isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8")}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-3xl -z-10" />
            
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1611703/img/multiscreen_device_stack.png" alt="HalfOrder app interface on multiple mobile devices showing meal splitting features" className="relative z-10 w-full max-w-md lg:max-w-full drop-shadow-[0_20px_50px_rgba(255,102,0,0.3)]" />
          </div>
        </div>

        <div className={cn("relative transition-all duration-700 delay-500 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="grid grid-cols-2 md:flex md:flex-row md:justify-center md:items-start gap-y-12 md:gap-x-0">
            {devices.map((device, index) => <React.Fragment key={index}>
                <div data-index={index} className="flex flex-col items-center text-center px-4 md:px-8 lg:px-12 group">
                  <div className="mb-4 p-4 rounded-sm bg-card text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 border border-border/50 shadow-md">
                    {device.icon}
                  </div>
                  <h3 className="font-black text-foreground mb-1 tracking-tighter uppercase text-sm">
                    {device.label}
                  </h3>
                  <p className="text-xs text-muted-foreground font-mono">
                    {device.description}
                  </p>
                </div>
                {index < devices.length - 1 && <Separator orientation="vertical" className="hidden md:block h-24 self-center bg-border/20" />}
              </React.Fragment>)}
          </div>
        </div>
      </div>
    </section>;
}